// @flow

import { fetch as fetchPolyfill } from 'whatwg-fetch';
import purgeLocalStorageOfToken from './errorHandling';
import type { wrappedApiResponseType } from './apiTypes';
import { handleOffline, handleOnline } from '../store/sessionService';
import { baseUrl } from './fetch';
import EnvironmentEnum from '../components/Shared/Enum/EnvironmentEnum';

export function getHeaders(headers?: {} = {}) {
  const token = window.localStorage ? localStorage.getItem('token') : false;

  return {
    ...headers,
    ...(token ? { Authorization: `Token ${token}` } : {}),
    Accept: 'application/json; version=v2',
    'X-Version': `HitchPin Web ${
      process.env.REACT_APP_VERSION ||
      process.env.REACT_APP_COMMIT_HASH ||
      'development'
    }`,
  };
}

export const catchEmptyBodyError = (e: any) => {
  if (e instanceof TypeError) {
    return {};
  }
  throw e;
};

const responseParser = (response: Response): Promise<any> =>
  response.json().catch(e => {
    if (e instanceof SyntaxError) {
      return Promise.resolve({
        detail: 'Could not read response from server.',
      });
    }
    throw e;
  });

export async function fetchErrorThrower(
  response: Response,
): Promise<wrappedApiResponseType<*>> {
  handleOnline();
  if (!response.ok) {
    return Promise.reject(
      Object({
        status: response.status,
        statusText: response.statusText,
        body: await responseParser(response),
      }),
    );
  }

  return Promise.resolve({
    status: response.status,
    statusText: response.statusText,
    body: await responseParser(response),
  });
}

export const networkErrorHandler = (error: any) => {
  handleOffline();
  throw Object({
    status: error.status || 0,
    statusText: 'Network Failure',
    body: { detail: 'Network Failure' },
  });
};

export const hpfetchWithoutContentType = (
  url: string,
  init: { headers?: {} } = { headers: {} },
) =>
  // $FlowFixMe
  fetchPolyfill(`${baseUrl || ''}${url}`, {
    ...init,
    headers: {
      ...getHeaders(),
      // $FlowFixMe
      ...init.headers,
    },
  })
    // $FlowFixMe
    .then<wrappedApiResponseType<*>>(fetchErrorThrower, networkErrorHandler)
    .catch(error => {
      if (
        error.body &&
        error.body.detail &&
        error.body.detail === 'Invalid token.'
      ) {
        purgeLocalStorageOfToken();
      } else {
        throw error;
      }
    });

export const hpfetch = (
  url: string,
  init: { headers?: {} } = { headers: {} },
) =>
  hpfetchWithoutContentType(url, {
    ...init,
    headers: {
      ...init.headers,
      'Content-Type': 'application/json',
    },
  });

export const hpFetchNext = (url: string) => {
  // remove the domain and protocol from the next string
  const getDomainAndProtocolRegex = /^(?:https?:\/\/)?[^/]+\/(.*)$/;
  const pathOnly = url.replace(getDomainAndProtocolRegex, '$1');
  // format the query string for use in hpfetch
  const getFirstLeadingSlashRegex = /^\/+/;
  const finalUrl = pathOnly.replace(getFirstLeadingSlashRegex, '');
  return hpfetch(finalUrl);
};

export const hpXHR = (
  url: string,
  method: 'POST' | 'PUT' | 'PATCH' | 'GET',
  payload: FormData,
  progressCallback: ProgressEvent => void,
) => {
  return new Promise<any, any>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', progressCallback);
    xhr.open(
      method,
      url.indexOf('http') === 0 ? url : `${baseUrl || ''}${url}`,
    );
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (xhr.status === 0 || (xhr.status >= 200 && xhr.status < 400)) {
          resolve(xhr.responseText ? JSON.parse(xhr.responseText) : '');
        } else {
          // eslint-disable-next-line
          reject();
        }
      }
    };
    xhr.send(payload);
  });
};

export const wpfetch = (url: string): Promise<wrappedApiResponseType<*>> =>
  fetchPolyfill(`https://blog.hitchpin.com/wp-json/wp/v2/${url}`)
    // $FlowFixMe
    .then<wrappedApiResponseType<*>>(fetchErrorThrower, null);

export const registerCardImpression = async (
  impressionPayload: Array<{
    u: string,
    i: number | null,
    p: number,
    b: number,
  }>,
) => {
  const environment = baseUrl.includes(EnvironmentEnum.Sandbox)
    ? `${EnvironmentEnum.Sandbox}.`
    : baseUrl.includes(EnvironmentEnum.Staging)
    ? `${EnvironmentEnum.Staging}.`
    : baseUrl.includes(EnvironmentEnum.Demo)
    ? `${EnvironmentEnum.Demo}.`
    : '';

  const url = `https://${environment}webh.hitchpin.com/cimp`;
  const apiKey = process.env.REACT_APP_IMPRESSIONS_API_KEY || '';

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': apiKey,
      },
      body: JSON.stringify(impressionPayload),
    });

    if (!response.ok) {
      throw new Error(`Impression HTTP Error! Status: ${response.status}`);
    }
  } catch (error) {
    console.error('Impression Fetch Error:', error);
  }
};

export const baseSiteUrl = baseUrl.includes(EnvironmentEnum.Sandbox)
  ? `https://${EnvironmentEnum.Sandbox}.hitchpin.com`
  : baseUrl.includes(EnvironmentEnum.Staging)
  ? `https://${EnvironmentEnum.Staging}.hitchpin.com`
  : baseUrl.includes(EnvironmentEnum.Demo)
  ? `https://${EnvironmentEnum.Demo}.hitchpin.com`
  : 'https://www.hitchpin.com';
